import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { IndividualPaymentSaveViewModel } from '@/shared/api/generated';
import {
  getApiV1IndividualpaymentFinancialPeopleFinancialpersonidEditorQueryKey,
  usePutApiV1IndividualpaymentFinancialPeopleFinancialpersonid,
} from '@/shared/api/generated';

import type { PlanWriteError } from '../utils/planWriteError';
import { classifyPlanWriteError } from '../utils/planWriteError';

/**
 * Salvamento do plano financeiro.
 *
 * Um único `PUT` carrega o conteúdo das três abas. Em caso de sucesso a query do
 * editor é invalidada — é ela que traz de volta os valores calculados pelo
 * backend, que a tela deliberadamente não recomputa.
 *
 * `isForbidden` distingue o `403` dos demais erros: o frontend não tem como
 * saber de antemão se o operador pode escrever (o login devolve apenas
 * `{ controllerId, controllerName }`, sem `canUpdate`), então a recusa só chega
 * aqui — e a tela precisa virar somente leitura em vez de mostrar erro genérico.
 *
 * @param financialPersonId - ID do contrato financeiro; `null` desabilita o envio.
 *
 * @example
 * ```tsx
 * const { save, isPending, isForbidden } = useSavePlan(101);
 * save(payload, { onSuccess: () => alert({ alertType: 'success', title: 'Salvo' }) });
 * ```
 */
export function useSavePlan(financialPersonId: number | null) {
  const queryClient = useQueryClient();
  const mutation = usePutApiV1IndividualpaymentFinancialPeopleFinancialpersonid();

  const save = useCallback(
    (
      data: IndividualPaymentSaveViewModel,
      options?: {
        readonly onSuccess?: () => void;
        readonly onError?: (error: PlanWriteError) => void;
      },
    ) => {
      if (financialPersonId === null) return;

      mutation.mutate(
        { financialPersonId, data },
        {
          onSuccess: () => {
            void queryClient.invalidateQueries({
              queryKey:
                getApiV1IndividualpaymentFinancialPeopleFinancialpersonidEditorQueryKey(
                  financialPersonId,
                ),
            });
            options?.onSuccess?.();
          },
          onError: error => options?.onError?.(classifyPlanWriteError(error)),
        },
      );
    },
    [financialPersonId, mutation, queryClient],
  );

  const failure = classifyPlanWriteError(mutation.error);

  return {
    save,
    isPending: mutation.isPending,
    isError: mutation.isError,
    isForbidden: failure.kind === 'forbidden',
    /** `true` no `5xx` — falha do servidor, não do que o operador preencheu. */
    isServerError: failure.kind === 'server',
    reset: mutation.reset,
  } as const;
}
